// Pristilbud (/offer-list), i startsidens stil (planer/ny-kundesamhandling-plan.md). Tilbud lages
// fra kundekortet («Lag pristilbud»), så de alltid hører til en kunde; her er oversikten.
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tilbake, Sidetopp, Valgknapper, feltKlasse } from './ny-kundesamhandling/Skjema';
import Ikon from './ny-kundesamhandling/Ikon';
import { kr, statusFor } from './tilbud/kalkyle';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const FILTRE = [
  { verdi: 'aktive', tittel: 'Aktive' },
  { verdi: 'godkjent', tittel: 'Godkjent' },
  { verdi: 'avslatt', tittel: 'Avslått' },
  { verdi: 'kansellert', tittel: 'Trukket tilbake' },
  { verdi: 'erstattet', tittel: 'Erstattet' },
  { verdi: 'alle', tittel: 'Alle' },
];
const AKTIVE = ['utkast', 'sendt', ''];

const sumFor = (o) => (o.produkter || []).reduce((s, p) => s + (Number(p.totalprisEtterRabatt) || 0), 0);
const fmtDato = (d) => (d ? new Date(d).toLocaleDateString('nb-NO') : '');

function OfferList() {
  const [offers, setOffers] = useState([]);
  const [lastet, setLastet] = useState(false);
  const [feil, setFeil] = useState('');
  const [filter, setFilter] = useState('aktive');
  const [sok, setSok] = useState('');

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/offers`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        // Eldre tilbud uten kopi av kunden: hent navnet én gang per kunde.
        const needsFetch = data.filter((o) => {
          const snap = o.customerSnapshot || {};
          return !snap.firstName && !snap.lastName && o.kundeid;
        });
        const customerMap = {};
        await Promise.all(
          [...new Set(needsFetch.map((o) => o.kundeid))].map(async (id) => {
            try {
              const r = await fetch(`${API_BASE_URL}/customers/${id}`);
              if (r.ok) {
                const c = await r.json();
                customerMap[id] = `${c.firstName || ''} ${c.lastName || ''}`.trim();
              }
            } catch (_) {}
          })
        );

        setOffers(
          data
            .map((offer) => {
              const snap = offer.customerSnapshot || {};
              const fromSnap = `${snap.firstName || ''} ${snap.lastName || ''}`.trim();
              const navn = fromSnap || customerMap[offer.kundeid] || (offer.klubbNavn ? '' : 'Ukjent kunde');
              // Pristilbud til en klubb: klubben først, så kontaktpersonen.
              return { ...offer, kundenavn: offer.klubbNavn ? [offer.klubbNavn, navn].filter(Boolean).join(' · ') : navn };
            })
            .sort((a, b) => new Date(b.registrertDato) - new Date(a.registrertDato))
        );
      } catch (error) {
        console.error('Feil ved henting av tilbud:', error.message);
        setFeil('Klarte ikke å hente tilbudene. Last siden på nytt om litt.');
      } finally {
        setLastet(true);
      }
    };
    fetchOffers();
  }, []);

  const liste = useMemo(() => {
    const q = sok.trim().toLowerCase();
    return offers
      .filter((o) => {
        const st = String(o.status || '');
        if (filter === 'aktive') return AKTIVE.includes(st);
        if (filter === 'alle') return true;
        return st === filter;
      })
      .filter((o) => !q || [
        o.kundenavn, o.offerRef, o.ansatt, o.customerSnapshot?.phoneNumber,
        ...(o.produkter || []).map((p) => p.artikkel),
      ].join(' ').toLowerCase().includes(q));
  }, [offers, filter, sok]);

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />
      <Sidetopp ikon="dokument" farge="betaling" tittel="Pristilbud" undertittel="Lag nye tilbud fra kundekortet («Lag pristilbud»)." />

      <section aria-label="Søk og utvalg" className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div>
          <label htmlFor="pt-sok" className="mb-1 block font-semibold text-slate-800">Søk i tilbudene</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"><Ikon navn="sok" className="w-6 h-6" /></span>
            <input id="pt-sok" type="search" value={sok} onChange={(e) => setSok(e.target.value)} placeholder="Kunde, telefon, ref., vare eller ansatt"
              aria-describedby="pt-sok-status" className={`${feltKlasse} pl-12`} />
          </div>
          <p id="pt-sok-status" role="status" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
            {lastet && sok.trim() ? `${liste.length} ${liste.length === 1 ? 'tilbud' : 'tilbud'} passer` : ''}
          </p>
        </div>
        <Valgknapper id="pt-filter" etikett="Status" valg={FILTRE} verdi={filter} onVelg={setFilter} />
      </section>

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      {!lastet ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter tilbud …</p>
      ) : liste.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">
          {sok.trim() ? 'Ingen tilbud passer søket.' : 'Ingen tilbud med denne statusen.'}
        </p>
      ) : (
        <ul className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {liste.map((o) => {
            const st = statusFor(o.status);
            const uleste = (o.messages || []).filter((m) => m.from === 'customer' && !m.readAt).length;
            const utlopt = AKTIVE.includes(String(o.status || '')) && o.utloper && new Date(o.utloper) < new Date();
            return (
              <li key={o._id}>
                <Link to={`/offer-details/${o._id}`} className="group flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-1.5 min-h-[2.75rem] hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-indigo-600/50">
                  <span className="min-w-0 flex-1 basis-56">
                    <span className="flex flex-wrap items-center gap-2">
                      {o.klubbNavn && <span className="rounded-full bg-violet-700 px-2.5 py-0.5 text-sm font-semibold text-white">Klubb</span>}
                      <span className="font-bold text-slate-900 [overflow-wrap:anywhere]">{o.kundenavn}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${st.farge}`}>{st.navn}</span>
                      {uleste > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-sm font-bold text-white">{uleste} {uleste === 1 ? 'ny melding' : 'nye meldinger'}</span>}
                    </span>
                    <span className="block text-sm text-slate-600 [overflow-wrap:anywhere]">
                      {[o.offerRef && `#${o.offerRef}`, o.versjon > 1 && `versjon ${o.versjon}`, `${(o.produkter || []).length} ${(o.produkter || []).length === 1 ? 'vare' : 'varer'}`, o.ansatt, `laget ${fmtDato(o.registrertDato)}`].filter(Boolean).join(' · ')}
                    </span>
                    {utlopt && <span className="block text-sm font-medium text-amber-900">Utløpt {fmtDato(o.utloper)}</span>}
                    {/* Sendt, men har kunden sett det? (portal.openCount, uten butikkens forhåndsvisninger) */}
                    {String(o.status) === 'sendt' && (
                      o.portal?.openCount
                        ? <span className="block text-sm font-medium text-emerald-800">Åpnet av kunden {fmtDato(o.portal.lastOpenedAt || o.portal.lastVisited)}</span>
                        : <span className="block text-sm text-slate-600">Ikke åpnet av kunden ennå</span>
                    )}
                  </span>
                  <span className="shrink-0 text-base font-bold text-slate-900">{kr(sumFor(o))}</span>
                  <Ikon navn="pil" className="w-5 h-5 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-indigo-700" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default OfferList;
